import { useEffect, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';

// FD-596 — make a cold start read as SLOW, not BROKEN.
//
// An idle Consumption-plan Manager wakes in 40–57 s (App Insights, `apc`,
// 2026-10-23); warm, the same calls take 1–2 s. Nothing failed — customers saw a
// skeleton that did not resolve, concluded the page was broken, and navigated
// away. The backend half of FD-596 shortens the cold start; this half makes the
// wait legible while it still happens.
//
// Two hooks, split so the timing POLICY is testable on its own:
//
//   useSlowLoading(active, delayMs) — the policy. True once `active` has been
//     CONTINUOUSLY true for `delayMs`; false the moment it stops. A warm load
//     (under the delay) therefore never shows anything — the notice must not
//     flash on the 1–2 s case, which is almost every load.
//
//   useInitialLoadInFlight() — the signal. True while any query with NO DATA YET
//     is fetching. Background refetches of data already on screen are excluded on
//     purpose: the page is not waiting on them, and a "still loading" line beside
//     a populated table would be the console saying something untrue. Mutations
//     are excluded too: some are legitimately long (a package build is ~71 s on a
//     WARM service), and telling an operator the service is "waking up" during
//     one would be a guess presented as a fact.

/** How long a load runs before the console says anything about it. */
export const SLOW_LOADING_DELAY_MS = 5_000;

/**
 * True once `active` has stayed true for `delayMs` without interruption.
 *
 * Resets to false synchronously-on-the-next-render when `active` goes false, and
 * the clock restarts from zero the next time it goes true: two 4 s loads back to
 * back are two warm loads, not one slow one.
 */
export function useSlowLoading(active: boolean, delayMs: number = SLOW_LOADING_DELAY_MS): boolean {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (!active) {
      setSlow(false);
      return undefined;
    }
    const timer = setTimeout(() => setSlow(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);

  // `active && slow` rather than `slow`: the effect above clears `slow` one
  // commit AFTER `active` drops, and that one frame would otherwise still render
  // the notice over a page that has just finished loading.
  return active && slow;
}

/**
 * True while at least one query that has never produced data is fetching.
 *
 * `status === 'pending'` is React Query v5's "no data yet" — it holds through
 * retries until the first success or the final error, which is exactly the span
 * an operator is looking at a skeleton.
 */
export function useInitialLoadInFlight(): boolean {
  const count = useIsFetching({ predicate: (query) => query.state.status === 'pending' });
  return count > 0;
}
